import React, { useState } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Routes, Route, useNavigate } from 'react-router-dom';
import Creation from './Creation';
import './App.css';

function HomePage() {
  const { scrollY } = useScroll();
  const heroOpacity = useTransform(scrollY, [0, 500], [1, 0]);
  const heroY = useTransform(scrollY, [0, 500], [0, 150]);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const smoothScrollTo = (id) => {
    setMenuOpen(false);
    const target = document.getElementById(id);
    if (!target) return;
    const start = window.scrollY;
    const end = target.getBoundingClientRect().top + window.scrollY;
    const distance = end - start;
    const duration = 1200;
    let startTime = null;
    const easeInOutCubic = (t) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      window.scrollTo(0, start + distance * easeInOutCubic(progress));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  return (
    <div className="app">

      {/* HEADER */}
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, ease: 'easeOut' }}
      >
        <h1>Ligne & Matière</h1>

        {/* NAV DESKTOP */}
        <nav className="nav-desktop">
          <a onClick={() => smoothScrollTo('creations')}>Créations</a>
          <a onClick={() => smoothScrollTo('apropos')}>À propos</a>
          <a onClick={() => smoothScrollTo('contact')}>Contact</a>
        </nav>

        {/* BURGER MOBILE */}
        <button
          className="burger"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Menu"
        >
          <span style={{ transform: menuOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none' }} />
          <span style={{ opacity: menuOpen ? 0 : 1 }} />
          <span style={{ transform: menuOpen ? 'rotate(-45deg) translate(5px, -5px)' : 'none' }} />
        </button>
      </motion.header>

      {/* MENU MOBILE DÉROULANT */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="nav-mobile"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <a onClick={() => smoothScrollTo('creations')}>Créations</a>
            <a onClick={() => smoothScrollTo('apropos')}>À propos</a>
            <a onClick={() => smoothScrollTo('contact')}>Contact</a>
          </motion.div>
        )}
      </AnimatePresence>

      {/* HERO */}
      <section id="hero">
        <motion.div className="hero-content" style={{ opacity: heroOpacity, y: heroY }}>
          <motion.span
            className="hero-sub"
            initial={{ opacity: 0, letterSpacing: '2px' }}
            animate={{ opacity: 1, letterSpacing: '8px' }}
            transition={{ duration: 2, delay: 0.5 }}
          >
            Artisanat & Création
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.3 }}
          >
            L'Art du Bois <br /> à l'état pur
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.5, delay: 0.8 }}
          >
            Mobilier artisanal • Création unique • Savoir-faire belge
          </motion.p>
          <motion.a
            onClick={() => smoothScrollTo('contact')}
            className="btn"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.2 }}
            whileHover={{ scale: 1.05 }}
          >
            Demander un devis
          </motion.a>
        </motion.div>
        <FloatingParticles />
      </section>

      {/* CRÉATIONS */}
      <section id="creations">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          Créations
        </motion.h2>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          style={{ textAlign: 'center', marginTop: '40px' }}
        >
          <motion.button
            className="btn"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => navigate('/creations')}
          >
            Nos Créations
          </motion.button>
        </motion.div>
      </section>

      {/* À PROPOS */}
      <section id="apropos" className="apropos">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          À propos
        </motion.h2>
        <motion.div
          className="apropos-content"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <p>
            Depuis plus de 30 ans, nous évoluons dans l'univers du bois et de la décoration.
            Ce qui a commencé comme une vocation est devenu au fil des années un véritable art de vivre,
            transmis par le geste, la patience et l'amour des belles matières. Il y a une dizaine d'années,
            nous avons franchi une nouvelle étape en créant nos propres meubles, entièrement faits main, pièce par pièce, dans notre atelier.
            Chaque création est pensée dans ses moindres détails, des assemblages aux finitions, pour garantir une pièce unique qui traversera le temps.
            Ce qui nous distingue, c'est aussi notre rapport particulier à la matière première.
            Au fil des années, nous avons constitué un stock important de bois nobles et rares, des essences soigneusement sélectionnées,
            parfois introuvables ailleurs, chacune avec son caractère propre, ses veines, ses teintes et son histoire.
            Chêne massif, noyer, frêne, ou essences plus rares encore, nous choisissons chaque planche avec exigence, en sachant déjà quel meuble elle pourrait devenir.
             Parce que chaque espace est unique et chaque client différent, nous proposons une création entièrement sur mesure. Vous venez avec une idée, une esquisse, une photo ou simplement une envie,
            nous vous accompagnons de la conception à la livraison pour créer le meuble que vous avez toujours imaginé. Dimensions, essences, finitions, style, tout est pensé ensemble, pour un résultat qui vous ressemble et qui durera des générations
          </p>
        </motion.div>
      </section>

      {/* CONTACT */}
      <section id="contact">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          Contact
        </motion.h2>
        <motion.form
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <input type="text" placeholder="Votre nom" />
          <input type="email" placeholder="Votre email" />
          <textarea placeholder="Votre message" />
          <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
            Envoyer
          </motion.button>
        </motion.form>
      </section>

      {/* FOOTER */}
      <footer>
        <p>© 2025 Ligne & Matière — Tous droits réservés</p>
      </footer>

    </div>
  );
}

function CreationsPage() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="app">
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, ease: 'easeOut' }}
      >
        <h1>Ligne & Matière</h1>

        <nav className="nav-desktop">
          <a onClick={() => navigate('/')}>Accueil</a>
        </nav>

        <button
          className="burger"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Menu"
        >
          <span style={{ transform: menuOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none' }} />
          <span style={{ opacity: menuOpen ? 0 : 1 }} />
          <span style={{ transform: menuOpen ? 'rotate(-45deg) translate(5px, -5px)' : 'none' }} />
        </button>
      </motion.header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="nav-mobile"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <a onClick={() => navigate('/')}>Accueil</a>
          </motion.div>
        )}
      </AnimatePresence>

      <Creation />
    </div>
  );
}

/* PARTICULES FLOTTANTES */
function FloatingParticles() {
  const particles = Array.from({ length: 12 }, (_, i) => ({
    id: i,
    left: `${10 + i * 7.5}%`,
    top: `${10 + (i % 4) * 20}%`,
    duration: 4 + (i % 4),
    delay: i * 0.3,
    x: i % 2 === 0 ? 10 : -10,
  }));

  return (
    <div className="particles">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="particle"
          animate={{ y: [0, -30, 0], x: [0, p.x, 0], opacity: [0.3, 0.8, 0.3] }}
          transition={{ duration: p.duration, repeat: Infinity, delay: p.delay, ease: 'easeInOut' }}
          style={{ left: p.left, top: p.top }}
        />
      ))}
    </div>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/creations" element={<CreationsPage />} />
    </Routes>
  );
}

export default App;
